import { useState, useEffect } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, IconButton, CircularProgress, Alert, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  InputAdornment, Tooltip, MenuItem, Checkbox,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Warning as WarningIcon,
  Description as NoticeIcon,
} from '@mui/icons-material';
import api from '../../config/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useStore from '../../config/store';
import { InfoTip } from '../LabelWithInfo';
import formatRut from '../../erp/formatRut';
import { formatDate, formatDateTime, formatDateRange } from '../formatDate';
import {
  EXPIRATION_NOTICE_CAUSE_CODE,
  daysUntil,
  todayIso,
  missingReasons,
  downloadTerminationNotices,
} from './terminationNotices';

/**
 * Estado del contrato en Talana (export por API): En Talana (persona + contrato
 * creados, tooltip con ids), Error (último intento fallido, tooltip con el
 * mensaje de Talana) o Sin Talana (nunca enviado).
 */
export const ContractTalanaChip = ({ talana, size = 'small' }) => {
  const { t } = useTranslation();
  if (talana?.contratoId) {
    return (
      <Tooltip title={t('erp.contracts.talana.tipExported', {
        persona: talana.personaId, contrato: talana.contratoId,
        env: talana.environment || '', at: talana.exportedAt ? formatDateTime(talana.exportedAt) : '',
      })}>
        <Chip size={size} color="success" variant="outlined" label={t('erp.contracts.talana.exported')} />
      </Tooltip>
    );
  }
  if (talana?.lastError) {
    return (
      <Tooltip title={t('erp.contracts.talana.tipFailed', { error: talana.lastError.message || '', step: talana.lastError.path || '' })}>
        <Chip size={size} color="error" variant="outlined" label={t('erp.contracts.talana.failed')} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={t('erp.contracts.talana.tipNone')}>
      <Chip size={size} variant="outlined" label={t('erp.clients.talana.none')} />
    </Tooltip>
  );
};

/**
 * Término / vencimiento en el listado (PLAN_ERP.md §15): causal y fecha si
 * tiene término; si no, aviso de vencido o de que vence en 30 días o menos.
 */
const TerminationChip = ({ contract }) => {
  const { t } = useTranslation();
  const termination = contract.termination;
  if (termination) {
    return (
      <Tooltip title={termination.causeName || ''}>
        <Chip size="small" label={`${termination.causeCode} · ${formatDate(termination.terminationDate)}`} />
      </Tooltip>
    );
  }
  const endDate = contract.dates?.endDate;
  if (!endDate) return '—';
  const days = daysUntil(endDate);
  if (days < 0) {
    return <Chip size="small" color="error" variant="outlined" label={t('erp.contracts.termination.chipExpired')} />;
  }
  if (days <= 30) {
    return (
      <Chip size="small" color="warning" variant="outlined"
        label={days === 0 ? t('erp.contracts.termination.chipExpiresToday') : t('erp.contracts.termination.chipExpiresIn', { count: days })} />
    );
  }
  return '—';
};

// Filtro de vigencia: vencen en N días (con o sin término, para imprimir
// juntos los avisos de esos días), vencidos sin término o terminados
const matchesValidity = (contract, filter) => {
  if (!filter) return true;
  if (filter === 'terminated') return Boolean(contract.termination);
  const endDate = contract.dates?.endDate;
  if (!endDate) return false;
  const days = daysUntil(endDate);
  if (filter === 'expired') return !contract.termination && days < 0;
  const limit = Number(filter.replace('expiring', ''));
  return days >= 0 && days <= limit;
};

// Mismo tope que el backend (validateTerminationNotices): avisos por PDF
const MAX_NOTICES_PER_FILE = 100;

const isNoticeEligible = (contract) => contract.termination?.causeCode === EXPIRATION_NOTICE_CAUSE_CODE;


/**
 * Texto del error al eliminar un contrato (lista y detalle): exportado a
 * remuneraciones, o enviado a firma en Izydocs (hay que anular el envío; si ya
 * está firmado no se puede eliminar).
 */
export const contractDeleteErrorMessage = (t, body) => {
  if (body?.code === 'CONTRACT_EXPORTED') return t('erp.contracts.deleteExported');
  if (body?.code === 'CONTRACT_HAS_SIGNATURE') {
    return t(body.signatureStatus === 'completed' ? 'erp.contracts.deleteSigned' : 'erp.contracts.deleteHasSignature');
  }
  return t('erp.contracts.deleteError');
};
export const EXPORT_STATUS_COLORS = {
  pending: 'warning',
  exported: 'success',
  modified_after_export: 'error',
};

/**
 * Contratos (spec 3.6-3.7): la llave con remuneraciones. Los contratos se
 * crean desde la ficha validada (detalle de ficha → Pasar a contrato).
 */
const ErpContractsLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { organization, authToken, showAlert } = useStore.getState();

  const [contracts, setContracts] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [exportFilter, setExportFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [validityFilter, setValidityFilter] = useState('');
  const [selected, setSelected] = useState([]); // contratos con 159-4 para descargar avisos
  const [noticeIssues, setNoticeIssues] = useState(null); // 422: { issues, readyContractIds }
  const [downloadingNotices, setDownloadingNotices] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/erp/contracts', {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        setContracts(response.data.response.data || []);
      } else {
        setError(t('erp.contracts.loadError'));
      }
    } catch (err) {
      console.error(err);
      setError(t('erp.contracts.loadError'));
    } finally {
      setLoading(false);
      }
  };

  useEffect(() => {
    if (organization) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      const response = await api.delete(`/erp/contracts/${deleteTarget.uid}`, {
        params: { organizationId: organization },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (response.data.response?.status_response === 'OK') {
        showAlert(t('erp.contracts.deleteSuccess'), 'success');
        setDeleteTarget(null);
        load();
      }
    } catch (err) {
      console.error(err);
      showAlert(contractDeleteErrorMessage(t, err.response?.data?.response), 'error');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    let list = contracts;
    if (exportFilter) list = list.filter((c) => c.exportStatus === exportFilter);
    if (validityFilter) list = list.filter((c) => matchesValidity(c, validityFilter));
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (c) =>
          c.workerName?.toLowerCase().includes(term) ||
          c.workerRut?.toLowerCase().includes(term) ||
          c.clientName?.toLowerCase().includes(term)
      );
    }
    setFiltered(list);
    // La selección de avisos solo conserva lo que sigue visible
    setSelected((prev) => prev.filter((id) => list.some((c) => c.uid === id && isNoticeEligible(c))));
  }, [contracts, exportFilter, validityFilter, searchTerm]);

  const eligibleVisible = filtered.filter(isNoticeEligible);
  const toggleSelected = (uid) =>
    setSelected((prev) => (prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]));
  const toggleAllSelected = () =>
    setSelected((prev) => (prev.length === eligibleVisible.length ? [] : eligibleVisible.map((c) => c.uid)));

  // Avisos de vencimiento: un PDF con una hoja por persona, en el orden del listado
  const handleDownloadNotices = async (contractIds) => {
    try {
      setDownloadingNotices(true);
      const single = contracts.find((c) => c.uid === contractIds[0]);
      await downloadTerminationNotices({
        organization,
        authToken,
        contractIds,
        fileName: contractIds.length === 1
          ? `aviso_vencimiento_${single?.workerRut || contractIds[0]}.pdf`
          : `avisos_vencimiento_${todayIso()}.pdf`,
      });
      setNoticeIssues(null);
    } catch (err) {
      if (err.code === 'NOTICE_DATA_MISSING') {
        setNoticeIssues({ issues: err.issues, readyContractIds: err.readyContractIds });
      } else {
        console.error(err);
        showAlert(t('erp.contracts.termination.noticeError'), 'error');
      }
    } finally {
      setDownloadingNotices(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 1 }}>{t('erp.contracts.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('erp.contracts.subtitle')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" placeholder={t('erp.contracts.searchPlaceholder')}
          value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ maxWidth: 380, flexGrow: 1 }}
          InputProps={{
            startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}><ClearIcon fontSize="small" /></IconButton>
              </InputAdornment>
            ),
          }} />
        <TextField select size="small" label={t('erp.contracts.table.exportStatus')} value={exportFilter}
          onChange={(e) => setExportFilter(e.target.value)} sx={{ width: 240 }}>
          <MenuItem value="">{t('erp.quotations.allStatuses')}</MenuItem>
          <MenuItem value="pending">{t('erp.contracts.exportStatus.pending')}</MenuItem>
          <MenuItem value="exported">{t('erp.contracts.exportStatus.exported')}</MenuItem>
          <MenuItem value="modified_after_export">{t('erp.contracts.exportStatus.modified_after_export')}</MenuItem>
        </TextField>
        <TextField select size="small" label={t('erp.contracts.termination.filter.label')} value={validityFilter}
          onChange={(e) => setValidityFilter(e.target.value)} sx={{ width: 220 }}>
          <MenuItem value="">{t('erp.contracts.termination.filter.all')}</MenuItem>
          <MenuItem value="expiring7">{t('erp.contracts.termination.filter.expiring7')}</MenuItem>
          <MenuItem value="expiring15">{t('erp.contracts.termination.filter.expiring15')}</MenuItem>
          <MenuItem value="expiring30">{t('erp.contracts.termination.filter.expiring30')}</MenuItem>
          <MenuItem value="expired">{t('erp.contracts.termination.filter.expired')}</MenuItem>
          <MenuItem value="terminated">{t('erp.contracts.termination.filter.terminated')}</MenuItem>
        </TextField>
        {selected.length > 0 && (
          <Tooltip title={selected.length > MAX_NOTICES_PER_FILE
            ? t('erp.contracts.termination.tooManyNotices', { max: MAX_NOTICES_PER_FILE })
            : ''}>
            <span>
              <Button variant="contained" size="small" startIcon={<NoticeIcon />}
                disabled={downloadingNotices || selected.length > MAX_NOTICES_PER_FILE}
                onClick={() => handleDownloadNotices(filtered.filter((c) => selected.includes(c.uid)).map((c) => c.uid))}
                sx={{ textTransform: 'none' }}>
                {t('erp.contracts.termination.downloadNotices', { count: selected.length })}
              </Button>
            </span>
          </Tooltip>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
      ) : filtered.length === 0 ? (
        <Alert severity="info">{t('erp.contracts.empty')}</Alert>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E5E7EB' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Tooltip title={t('erp.contracts.termination.selectAllNotices')}>
                    <span>
                      <Checkbox size="small" disabled={eligibleVisible.length === 0}
                        checked={eligibleVisible.length > 0 && selected.length === eligibleVisible.length}
                        indeterminate={selected.length > 0 && selected.length < eligibleVisible.length}
                        onChange={toggleAllSelected} />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>{t('erp.contracts.table.worker')}</TableCell>
                <TableCell>{t('erp.contracts.table.rut')}</TableCell>
                <TableCell>{t('erp.contracts.table.client')}</TableCell>
                <TableCell>{t('erp.quotations.table.serviceMode')}</TableCell>
                <TableCell>{t('erp.contracts.table.cause')}</TableCell>
                <TableCell>{t('erp.contracts.table.dates')}</TableCell>
                <TableCell>{t('erp.contracts.termination.column')}</TableCell>
                <TableCell>{t('erp.contracts.table.causeDays')}<InfoTip title={t('erp.tooltips.contract.causeDays')} /></TableCell>
                <TableCell>{t('erp.contracts.table.exportStatus')}</TableCell>
                <TableCell>{t('erp.clients.table.talana')}</TableCell>
                <TableCell align="right">{t('erp.common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((contract) => (
                <TableRow key={contract.uid} hover sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/erp/contracts/${contract.uid}`)}>
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    {isNoticeEligible(contract) && (
                      <Checkbox size="small" checked={selected.includes(contract.uid)}
                        onChange={() => toggleSelected(contract.uid)}
                        inputProps={{ 'aria-label': t('erp.contracts.termination.selectForNotice') }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{contract.workerName || '—'}</TableCell>
                  <TableCell>{formatRut(contract.workerRut)}</TableCell>
                  <TableCell>{contract.clientName || '—'}</TableCell>
                  {/* Modalidad: mismos textos que el select de la cotización */}
                  <TableCell>{contract.serviceMode ? t(`erp.quotations.serviceMode.${contract.serviceMode}`) : '—'}</TableCell>
                  <TableCell>{contract.hiringCause?.code || '—'}</TableCell>
                  <TableCell>{formatDateRange(contract.dates?.startDate, contract.dates?.endDate, t('erp.contracts.openEnded'))}</TableCell>
                  <TableCell><TerminationChip contract={contract} /></TableCell>
                  <TableCell>
                    {contract.causeControl
                      ? `${contract.causeControl.daysConsumed}/${contract.causeControl.legalLimitDays ?? '∞'}`
                      : '—'}
                    {contract.causeControl?.warning && (
                      <Tooltip title={t('erp.contracts.balance.warning')}>
                        <WarningIcon color="error" fontSize="small" sx={{ ml: 0.5, verticalAlign: 'middle' }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={t(`erp.contracts.exportStatus.${contract.exportStatus}`)}
                      color={EXPORT_STATUS_COLORS[contract.exportStatus] || 'default'} />
                  </TableCell>
                  <TableCell><ContractTalanaChip talana={contract.talana} /></TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('erp.common.view')}>
                      <IconButton size="small"
                        onClick={(e) => { e.stopPropagation(); navigate(`/erp/contracts/${contract.uid}`); }}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {contract.exportStatus === 'pending' && (
                      <Tooltip title={t('erp.contracts.deleteContract')}>
                        <IconButton size="small" color="error"
                          onClick={(e) => { e.stopPropagation(); setDeleteTarget(contract); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Avisos que no se pudieron generar (422 NOTICE_DATA_MISSING) */}
      <Dialog open={Boolean(noticeIssues)} onClose={() => setNoticeIssues(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('erp.contracts.termination.noticeMissingTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>{t('erp.contracts.termination.noticeMissingIntro')}</Typography>
          {(noticeIssues?.issues || []).map((issue) => {
            const row = contracts.find((c) => c.uid === issue.contractId);
            return (
              <Typography key={issue.contractId} variant="body2" sx={{ mb: 0.5 }}>
                <strong>{issue.workerName || row?.workerName || (row?.workerRut && formatRut(row.workerRut)) || issue.contractId}:</strong>{' '}
                {missingReasons(t, issue.missing)}
              </Typography>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoticeIssues(null)} sx={{ textTransform: 'none' }}>
            {t('erp.common.close')}
          </Button>
          {(noticeIssues?.readyContractIds || []).length > 0 && (
            <Button variant="contained" startIcon={<NoticeIcon />} disabled={downloadingNotices}
              onClick={() => handleDownloadNotices(noticeIssues.readyContractIds)} sx={{ textTransform: 'none' }}>
              {t('erp.contracts.termination.noticeDownloadReady', { count: noticeIssues.readyContractIds.length })}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmación de eliminación (solo pendientes; revierte la cadena) */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('erp.contracts.deleteContract')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            {deleteTarget ? deleteTarget.workerName || formatRut(deleteTarget.workerRut) : ''}
          </Typography>
          <Typography variant="body2">{t('erp.contracts.deleteConfirmText')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} sx={{ textTransform: 'none' }}>
            {t('erp.common.cancel')}
          </Button>
          <Button variant="contained" color="error" disabled={deleting} onClick={handleDelete}
            sx={{ textTransform: 'none' }}>
            {t('erp.contracts.deleteContract')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ErpContractsLayout;
